import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { UserInvitationModel } from 'src/Libs/installation-rights-management/models'
import { ApiInviteType } from 'src/Libs/api/types'
import { SettingsBox } from '../Data'
import useInvitationsDialog from './hooks/use-invitations-dialog'
import { getInvitationMessage } from './utils'

interface Props {
  userInvitations?: UserInvitationModel[]
  isUserInvitationLoading?: boolean
  invitationToken?: string
  onClose?: () => void
}

interface DialogContentProps {
  isDataLoading: boolean
  invitations: UserInvitationModel[]
  activeToken: string | null
  isAcceptLoading: boolean
  isDeclineLoading: boolean
  onAccept: (tokens?: string[]) => void
  onDecline: (token?: string) => void
  isAwaitingRefetch?: boolean
}

const InvitationsDialog: React.FC<Props> = ({ userInvitations, invitationToken, onClose, isUserInvitationLoading }) => {
  const { t } = useCustomTranslation()
  const { invitations, isDataLoading, isAcceptLoading, isAwaitingRefetch, isDeclineLoading, open, activeToken, onAccept, onDecline, handleClose } =
    useInvitationsDialog({ userInvitations, invitationToken, onClose, isUserInvitationLoading })

  if (!open) return null

  const isSingle = invitations.length === 1

  return (
    <Dialog
      closeOnBackdropClick={false}
      showCloseButton={invitations.length === 0 && !isDataLoading}
      title={t('installation.invitation.title')}
      onClose={handleClose}
      actions={{
        primary: {
          name: t('installation.invitation.primaryButtonText'),
          onClick: () => onAccept(),
          isLoading: isAcceptLoading || isAwaitingRefetch,
        },
        secondary: [
          {
            name: t('installation.invitation.secondaryButtonText'),
            onClick: () => onDecline(),
            isLoading: isDeclineLoading,
          },
        ],
      }}
      useActions={isSingle}
    >
      <DialogContent
        isDataLoading={isDataLoading}
        invitations={invitations}
        activeToken={activeToken}
        isAcceptLoading={isAcceptLoading}
        isDeclineLoading={isDeclineLoading}
        onAccept={onAccept}
        onDecline={onDecline}
        isAwaitingRefetch={isAwaitingRefetch}
      />
    </Dialog>
  )
}

export default InvitationsDialog

const DialogContent: React.FC<DialogContentProps> = ({
  isDataLoading,
  invitations,
  activeToken,
  isAcceptLoading,
  isDeclineLoading,
  onAccept,
  onDecline,
  isAwaitingRefetch,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const invitationMessage = getInvitationMessage(invitations, t)
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'))

  if (isDataLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress />
      </Box>
    )
  }

  if (isAwaitingRefetch) {
    return (
      <Stack justifyContent="center" alignItems={'center'} py={2} gap={2}>
        <CircularProgress />
        <Typography>{t('installation.invitation.processing')}</Typography>
      </Stack>
    )
  }

  if (invitations.length === 1) {
    return <>{invitationMessage}</>
  }

  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ flex: 1, minWidth: '100%' }}>
      <Typography variant="cardSubtitle">{invitationMessage}</Typography>
      {invitations.map(invitation => {
        const token = invitation.invitationToken!
        const isActiveInvitation = activeToken === token
        const isHandover = invitation.inviteType === ApiInviteType.handover || invitation.inviteType === ApiInviteType.handoverPlanned
        const description = isHandover ? t('installation.invitation.handover') : ''
        const installationName = invitation.projectName ?? invitation.partnersInstallationName
        const title =
          isSmUp && installationName
            ? t('installation.invitation.item-title', { id: invitation.installationId, name: installationName })
            : t('installation.invitation.item-short-title', { id: invitation.installationId })

        return (
          <SettingsBox
            key={token}
            title={title}
            titleSx={{ whiteSpace: { xs: 'pre-line', sm: 'nowrap' } }}
            description={description}
            suffix={
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', sm: 'auto' }} gap={1}>
                <ButtonWithLoading
                  label={t('installation.invitation.secondaryButtonText')}
                  variant="outlined"
                  color="inherit"
                  onClick={() => onDecline(token)}
                  isLoading={isActiveInvitation && isDeclineLoading}
                  disabled={activeToken !== null && !isActiveInvitation}
                  fullWidth
                />
                <ButtonWithLoading
                  label={t('installation.invitation.primaryButtonText')}
                  variant="contained"
                  color="primary"
                  onClick={() => onAccept([token])}
                  isLoading={isActiveInvitation && isAcceptLoading}
                  disabled={activeToken !== null && !isActiveInvitation}
                  fullWidth
                />
              </Box>
            }
          />
        )
      })}
    </Box>
  )
}
